// SPDX-License-Identifier: AGPL-3.0-or-later

import styles from '@app/features/shop/components/modals/UsernameCardNameStep.module.css';
import {ShopPriceLine} from '@app/features/shop/components/ShopPriceLine';
import type {UsernameNameRefusal} from '@app/features/shop/hooks/useShopCheckout';
import type {UsernameCardAvailability} from '@app/features/shop/hooks/useUsernameCardAvailability';
import {type ShopUsernameCardEntry, usernameSku} from '@app/features/shop/utils/ShopEntries';
import {
	CHECKING_AVAILABILITY_DESCRIPTOR,
	NAME_CANNOT_BE_USED_DESCRIPTOR,
	NAME_CHECK_FAILED_DESCRIPTOR,
	NAME_IS_ALREADY_YOURS_DESCRIPTOR,
	NAME_IS_FREE_DESCRIPTOR,
	NAME_IS_HELD_DESCRIPTOR,
	NAME_IS_HELD_FOR_PAYMENT_DESCRIPTOR,
	NAME_IS_HELD_FOR_YOU_DESCRIPTOR,
	NAME_IS_HELD_UNTIL_DESCRIPTOR,
	NAME_IS_TAKEN_DESCRIPTOR,
	PICK_A_FREE_SHORT_NAME_DESCRIPTOR,
	TAKE_FREE_WITH_VISIONARY_DESCRIPTOR,
	USERNAME_CARD_EXACT_LENGTH_DESCRIPTOR,
	USERNAME_CARD_HINT_DESCRIPTOR,
	USERNAME_CARD_NOT_ON_SALE_DESCRIPTOR,
	USERNAME_LABEL_DESCRIPTOR,
} from '@app/features/shop/utils/ShopMessageDescriptors';
import {Button} from '@app/features/ui/button/Button';
import {Input} from '@app/features/ui/components/form/FormInput';
import type {MessageDescriptor} from '@lingui/core';
import {useLingui} from '@lingui/react/macro';
import {useId} from 'react';

const STATUS_MESSAGES: Record<UsernameCardAvailability['state'], MessageDescriptor> = {
	idle: USERNAME_CARD_HINT_DESCRIPTOR,
	invalid: USERNAME_CARD_EXACT_LENGTH_DESCRIPTOR,
	checking: CHECKING_AVAILABILITY_DESCRIPTOR,
	available: NAME_IS_FREE_DESCRIPTOR,
	held_by_you: NAME_IS_HELD_FOR_YOU_DESCRIPTOR,
	owned: NAME_IS_ALREADY_YOURS_DESCRIPTOR,
	taken: NAME_IS_TAKEN_DESCRIPTOR,
	held: NAME_IS_HELD_DESCRIPTOR,
	not_allowed: NAME_CANNOT_BE_USED_DESCRIPTOR,
	closed: USERNAME_CARD_NOT_ON_SALE_DESCRIPTOR,
	error: NAME_CHECK_FAILED_DESCRIPTOR,
};

const TONES: Partial<Record<UsernameCardAvailability['state'], 'ok' | 'refused'>> = {
	available: 'ok',
	held_by_you: 'ok',
	taken: 'refused',
	held: 'refused',
	not_allowed: 'refused',
	closed: 'refused',
	error: 'refused',
};

// A held name says why: a payment being confirmed, or another buyer's order and the minutes left of its hold.
function statusMessage(state: UsernameCardAvailability['state'], availability: UsernameCardAvailability) {
	if (state !== 'held' || availability.state !== 'held') return {descriptor: STATUS_MESSAGES[state], values: {}};
	if (availability.awaitingPayment) return {descriptor: NAME_IS_HELD_FOR_PAYMENT_DESCRIPTOR, values: {}};
	if (!availability.heldUntil) return {descriptor: NAME_IS_HELD_DESCRIPTOR, values: {}};
	const minutes = Math.max(1, Math.ceil((Date.parse(availability.heldUntil) - Date.now()) / 60_000));
	return {descriptor: NAME_IS_HELD_UNTIL_DESCRIPTOR, values: {minutes}};
}

interface UsernameCardNameStepProps {
	length: 1 | 2 | 3;
	value: string;
	onChange: (value: string) => void;
	availability: UsernameCardAvailability;
	// The order's refusal of this name, shown until the name's new check answers.
	refusal: UsernameNameRefusal | null;
	// A named username or an offer: the name is given, checked, and never typed.
	fixed?: boolean;
	// The name's price shows under its answer; off where the dialog shows the price itself or sells nothing.
	showPrice?: boolean;
	// Shown when the checked name may be taken free with the Visionary username right.
	onTakeFree?: () => void;
}

// The name field of a username card: lowercase as typed, the card's length at most, one check after typing stops.
export function UsernameCardNameStep({
	length,
	value,
	onChange,
	availability,
	refusal,
	fixed = false,
	showPrice = true,
	onTakeFree,
}: UsernameCardNameStepProps) {
	const {i18n} = useLingui();
	const statusId = useId();
	const state = refusal && availability.state === 'checking' ? refusal : availability.state;
	const free = state === 'available' || state === 'held_by_you';
	const status = statusMessage(state, availability);
	// Shown like its length's card: the shop's price line on the name's own price.
	const priced: ShopUsernameCardEntry | null =
		showPrice && free && availability.price
			? {kind: 'username', sku: usernameSku(length), length, price: availability.price, asset: null, visible: true}
			: null;
	return (
		<div className={styles.step} data-flx="shop.username-card-name-step.step">
			{!fixed && (
				<p className={styles.lead} data-flx="shop.username-card-name-step.lead">
					{i18n._(PICK_A_FREE_SHORT_NAME_DESCRIPTOR)}
				</p>
			)}
			<Input
				label={i18n._(USERNAME_LABEL_DESCRIPTOR)}
				value={value}
				onChange={(event) => onChange(event.target.value.toLowerCase())}
				type="text"
				autoComplete="off"
				autoCapitalize="none"
				autoCorrect="off"
				spellCheck={false}
				maxLength={length}
				readOnly={fixed}
				leftElement={
					<span aria-hidden="true" data-flx="shop.username-card-name-step.at">
						@
					</span>
				}
				aria-describedby={statusId}
				data-flx="shop.username-card-name-step.input"
			/>
			<p
				id={statusId}
				className={styles.status}
				data-tone={TONES[state]}
				role="status"
				aria-live="polite"
				data-flx="shop.username-card-name-step.status"
			>
				{i18n._(status.descriptor, {length, ...status.values})}
			</p>
			{priced && (
				<div className={styles.price} data-flx="shop.username-card-name-step.price">
					<ShopPriceLine entry={priced} size="dialog" data-flx="shop.username-card-name-step.shop-price-line" />
				</div>
			)}
			{showPrice && free && !availability.price && availability.visionaryFree && (
				<p className={styles.status} data-tone="refused" data-flx="shop.username-card-name-step.not-sold">
					{i18n._(USERNAME_CARD_NOT_ON_SALE_DESCRIPTOR)}
				</p>
			)}
			{free && availability.visionaryFree && onTakeFree && (
				<Button
					variant="secondary"
					small
					fitContent
					onClick={onTakeFree}
					data-flx="shop.username-card-name-step.button.take-free"
				>
					{i18n._(TAKE_FREE_WITH_VISIONARY_DESCRIPTOR)}
				</Button>
			)}
		</div>
	);
}
